import { useCallback, useMemo } from 'react';
import { Filter, getEmptyFilterExpression, StringConditionType } from '@fabric-ui/components';
import { isDeepEqual } from '@fabric-ui/utils';
import {
  addGlobalOperand,
  addInlineOperand,
  countExpressions,
  deleteOperandAt,
  getUniqueFieldNames,
  replaceOperandAt,
  toggleAllLogicalOperators,
} from '../expressionOperations';
import { useFilterConfig, useFilterContext } from '../FilterContext';
import { LogicalOperatorType } from '../types';

type FilterOperand = NonNullable<Filter['and']>['operands'][number];

interface UseExpressionOperationsOptions {
  canDeleteLast?: boolean;
  defaultEmptyFilter?: Filter;
  defaultField?: string | null;
  fieldSelectionLimit?: { limit: number; message: string };
  hasListTab?: boolean;
  level?: number;
  rootOperator: LogicalOperatorType;
  rowsLimit?: { limit: number; message: string };
  supportParser?: boolean;
}

export const useExpressionOperations = (
  filter: Filter,
  setFilter: ((filter?: Filter) => void) | undefined,
  options: UseExpressionOperationsOptions
) => {
  const { disabled } = useFilterConfig();
  const { accountEntities } = useFilterContext();
  const fieldMap = accountEntities?.fieldMap ?? {};

  const updateFilter = useCallback((nextFilter?: Filter) => setFilter?.(nextFilter), [setFilter]);

  const emptyExpressionOptions = useMemo(
    () => ({
      defaultEmptyFilter: options.defaultEmptyFilter,
      defaultField: options.defaultField,
      fieldMap,
      supportParser: options.supportParser,
    }),
    [fieldMap, options.defaultEmptyFilter, options.defaultField, options.supportParser]
  );

  const addGlobal = useCallback(
    (operator: LogicalOperatorType) => updateFilter(addGlobalOperand(filter, operator, emptyExpressionOptions)),
    [emptyExpressionOptions, filter, updateFilter]
  );

  const toggleOperators = useCallback(
    () => updateFilter(toggleAllLogicalOperators(filter, options.hasListTab)),
    [filter, options.hasListTab, updateFilter]
  );

  const replaceAt = useCallback(
    (index: number, value: FilterOperand) => updateFilter(replaceOperandAt(filter, options.rootOperator, index, value)),
    [filter, options.rootOperator, updateFilter]
  );

  const deleteAt = useCallback(
    (index: number) =>
      updateFilter(
        deleteOperandAt(filter, options.rootOperator, index, {
          canDeleteLast: options.canDeleteLast,
          defaultEmptyFilter: options.defaultEmptyFilter,
          defaultField: options.defaultField,
          hasListTab: options.hasListTab,
          level: options.level,
          supportParser: options.supportParser,
        })
      ),
    [
      filter,
      options.canDeleteLast,
      options.defaultEmptyFilter,
      options.defaultField,
      options.hasListTab,
      options.level,
      options.rootOperator,
      options.supportParser,
      updateFilter,
    ]
  );

  const addInline = useCallback(
    (index: number) => updateFilter(addInlineOperand(filter, options.rootOperator, index, { fieldMap, level: options.level })),
    [fieldMap, filter, options.level, options.rootOperator, updateFilter]
  );

  const selectedFieldNames = useMemo(() => getUniqueFieldNames(filter), [filter]);
  const isOverFieldNamesLimit = !!options.fieldSelectionLimit && selectedFieldNames.size >= options.fieldSelectionLimit.limit;
  const isOverExpressionsLimit = !!options.rowsLimit && countExpressions(filter) >= options.rowsLimit.limit;
  const disableAddButton =
    isDeepEqual(getEmptyFilterExpression(null, options.supportParser ? StringConditionType.EQUALS : undefined), filter) ||
    isOverExpressionsLimit ||
    disabled;

  return {
    addGlobal,
    addInline,
    deleteAt,
    disableAddButton,
    isOverExpressionsLimit,
    isOverFieldNamesLimit,
    replaceAt,
    selectedFieldNames,
    toggleOperators,
  };
};
